import React, { useState, useEffect, useCallback } from 'react';
import axios from 'axios';
import JsBarcode from 'jsbarcode';
import { setStation, connect, getPrinters, printHTML, isAvailable } from '../../utils/qzPrint';
import './PickTicketScreen.css';

// What the age counter is counting from (server decides: paid date, ship date, or order date).
function ageTooltip(order) {
  const d = order.age_basis_date
    ? new Date(order.age_basis_date).toLocaleDateString('en-US', { month: 'short', day: 'numeric' })
    : '';
  if (order.age_basis === 'paid') return `Days since paid${d ? ` (${d})` : ''}`;
  if (order.age_basis === 'ship_date') return `Days since ship date${d ? ` (${d})` : ''}`;
  return `Days since order date${d ? ` (${d})` : ''}`;
}


// '2026-09-26' -> '9/26'. Parsed as a plain date so the browser's timezone
// cannot shift it a day.
function formatCancelDate(ymd) {
  const [, m, d] = String(ymd).split('-');
  return `${Number(m)}/${Number(d)}`;
}

function PickTicketScreen() {
  const [queue, setQueue] = useState([]);
  const [stats, setStats] = useState({ printed_today: 0, active_now: 0, queue_count: 0 });
  const [loading, setLoading] = useState(true);
  const [error, setError] = useState(null);
  const [lastUpdate, setLastUpdate] = useState(null);
  const [refreshing, setRefreshing] = useState(false);

  // Filter
  const [filterType, setFilterType] = useState('all');

  // Free-text search (order #, customer name, memo, item SKU / name)
  const [search, setSearch] = useState('');
  // On-screen keyboard for the touchscreen stations (no physical keyboard)
  const [showKeyboard, setShowKeyboard] = useState(false);

  // QZ Tray
  const [qzConnected, setQzConnected] = useState(false);
  const [printers, setPrinters] = useState([]);
  const [selectedPrinter, setSelectedPrinter] = useState(() =>
    localStorage.getItem('pickTicketPrinter') || localStorage.getItem('laserPrinter') || ''
  );
  const [showPrinterModal, setShowPrinterModal] = useState(false);

  // Print state
  const [printingOrderId, setPrintingOrderId] = useState(null);

  // Expanded row state
  const [expandedOrderId, setExpandedOrderId] = useState(null);
  const [expandedItems, setExpandedItems] = useState([]);
  const [expandLoading, setExpandLoading] = useState(false);

  // SKU currently being broken from cases into singles (one at a time)
  const [convertingSku, setConvertingSku] = useState(null);

  // Adjust on-hand modal (click the OH figure in an expanded row)
  const [adjustItem, setAdjustItem] = useState(null);
  const [adjustQty, setAdjustQty] = useState('');
  const [adjusting, setAdjusting] = useState(false);
  const [adjustError, setAdjustError] = useState('');

  // Toast
  const [toast, setToast] = useState(null);

  const showToast = (message, type = 'success') => {
    setToast({ message, type });
    setTimeout(() => setToast(null), 3000);
  };

  // Connect to QZ Tray on mount
  useEffect(() => {
    (async () => {
      try {
        await setStation('pick');
        const available = await isAvailable();
        if (available) {
          setQzConnected(true);
          const list = await getPrinters();
          setPrinters(list);
          // Auto-select saved printer or first available
          const saved = localStorage.getItem('pickTicketPrinter') || localStorage.getItem('laserPrinter');
          if (saved && list.includes(saved)) {
            setSelectedPrinter(saved);
          } else if (list.length > 0 && !saved) {
            setSelectedPrinter(list[0]);
            localStorage.setItem('pickTicketPrinter', list[0]);
          }
        }
      } catch (err) {
        console.warn('QZ Tray not available:', err.message);
      }
    })();
  }, []);

  // Fetch queue
  const fetchQueue = useCallback(async () => {
    try {
      const [regularRes, fbaRes] = await Promise.all([
        axios.get('/api/picking/queue'),
        axios.get('/api/picking/fba-queue').catch(() => ({ data: [] })),
      ]);
      setQueue([...regularRes.data, ...(fbaRes.data || [])]);
      setError(null);
    } catch (err) {
      console.error('Failed to fetch picking queue:', err);
      setError('Failed to load picking queue');
    }
  }, []);

  // Fetch stats
  const fetchStats = useCallback(async () => {
    try {
      const res = await axios.get('/api/picking/stats');
      setStats(res.data);
    } catch (err) {
      console.error('Failed to fetch picking stats:', err);
    }
  }, []);

  // Load all data
  const loadAll = useCallback(async (showLoader = true) => {
    if (showLoader) setLoading(true);
    await Promise.all([fetchQueue(), fetchStats()]);
    setLastUpdate(new Date());
    if (showLoader) setLoading(false);
  }, [fetchQueue, fetchStats]);

  // Initial load + auto-refresh every 60s
  useEffect(() => {
    loadAll();
    const interval = setInterval(() => loadAll(false), 60000);
    return () => clearInterval(interval);
  }, [loadAll]);

  // Manual refresh
  const handleRefresh = async () => {
    setRefreshing(true);
    try {
      const [regularRes, fbaRes] = await Promise.all([
        axios.get('/api/picking/queue'),
        axios.get('/api/picking/fba-queue').catch(() => ({ data: [] })),
      ]);
      setQueue([...regularRes.data, ...(fbaRes.data || [])]);
      const statsRes = await axios.get('/api/picking/stats');
      setStats(statsRes.data);
      setLastUpdate(new Date());
    } catch (err) {
      console.error('Refresh failed:', err);
    } finally {
      setRefreshing(false);
    }
  };

  // Pickability tier: 0 = fully pickable, 1 = partial, 2 = none
  const getPickabilityTier = (order) => {
    const shippable = order.shippable_line_items || 0;
    const total = order.total_line_items || 0;
    if (total > 0 && shippable === total) return 0;
    if (shippable > 0) return 1;
    return 2;
  };

  // NetSuite customer category IDs
  const AMAZON_CATS = [42];
  const PL_CATS = [39]; // Private Label
  const WHOLESALE_CATS = [26, 29, 30, 38]; // Select, Foundation, Key Accounts, Other
  // DTC = null/0/43/1 or anything not wholesale/amazon/PL

  const isDTC = (cat) => !cat || cat === 43 || cat === 1;

  // Mystery Box orders (Rewined Candle / Non-Candle, CF Mystery Box) get their
  // own tab and are kept out of every other tab, All included.
  const isMysteryBox = (order) => /mystery box/i.test(order.search_items || '');

  // Category filter
  const categoryFilteredQueue = queue.filter(order => {
    if (filterType === 'mysterybox') return isMysteryBox(order);
    if (isMysteryBox(order)) return false;
    if (filterType === 'all') return true;
    if (filterType === 'fba') return order.order_type === 'fba_inbound';
    const cat = Number(order.customer_category) || 0;
    if (filterType === 'dtc') return isDTC(cat) && order.order_type !== 'fba_inbound';
    if (filterType === 'wholesale') return WHOLESALE_CATS.includes(cat);
    if (filterType === 'amazon') return AMAZON_CATS.includes(cat) && order.order_type !== 'fba_inbound';
    if (filterType === 'privatelabel') return PL_CATS.includes(cat);
    if (filterType === 'localdelivery') return !!order.is_local_delivery;
    return true;
  });

  // Free-text search: every space-separated term must appear somewhere in the
  // order # / customer / memo / line-item text (SKUs + product names).
  const searchTerms = search.trim().toLowerCase().split(/\s+/).filter(Boolean);
  const filteredQueue = categoryFilteredQueue.filter(order => {
    if (searchTerms.length === 0) return true;
    const haystack = [
      order.tran_id,
      order.customer_name,
      order.memo,
      order.search_items,
    ].filter(Boolean).join('  ').toLowerCase();
    return searchTerms.every(term => haystack.includes(term));
  });

  // Sort: priority orders pinned first, then fully pickable, partial, none.
  // Secondary sort by oldest
  const sortedQueue = [...filteredQueue].sort((a, b) => {
    if (!!b.priority !== !!a.priority) return (b.priority ? 1 : 0) - (a.priority ? 1 : 0);
    const tierA = getPickabilityTier(a);
    const tierB = getPickabilityTier(b);
    if (tierA !== tierB) return tierA - tierB;
    return (b.age_days || 0) - (a.age_days || 0);
  });

  // Helpers
  const getCategoryLabel = (cat, orderType) => {
    if (orderType === 'fba_inbound') return 'FBA Inbound';
    const c = Number(cat) || 0;
    if (isDTC(c)) return 'DTC';
    if (AMAZON_CATS.includes(c)) return 'Amazon';
    if (PL_CATS.includes(c)) return 'Private Label';
    const names = { 26: 'Select', 29: 'Foundation', 30: 'Key Acct' };
    return names[c] || 'Wholesale';
  };

  const getCategoryClass = (cat, orderType) => {
    if (orderType === 'fba_inbound') return 'fba-inbound';
    const c = Number(cat) || 0;
    if (isDTC(c)) return 'dtc';
    if (AMAZON_CATS.includes(c)) return 'amazon';
    if (PL_CATS.includes(c)) return 'privatelabel';
    if (c === 30) return 'keyacct';
    return 'wholesale';
  };

  const getShippableInfo = (order) => {
    const shippable = order.shippable_line_items || 0;
    const total = order.total_line_items || 0;
    if (total === 0) return { label: '-', className: 'none' };
    if (shippable === total) return { label: `${shippable} / ${total}`, className: 'fully' };
    if (shippable > 0) return { label: `${shippable} / ${total}`, className: 'partial' };
    return { label: `0 / ${total}`, className: 'none' };
  };

  const formatDate = (dateString) => {
    if (!dateString) return '-';
    return new Date(dateString).toLocaleDateString('en-US', { month: 'short', day: 'numeric' });
  };

  const formatPrintedTime = (dateString) => {
    if (!dateString) return '-';
    const d = new Date(dateString);
    const now = new Date();
    const diffMs = now - d;
    const diffMin = Math.floor(diffMs / 60000);
    if (diffMin < 1) return 'Just now';
    if (diffMin < 60) return `${diffMin}m ago`;
    const diffHrs = Math.floor(diffMin / 60);
    if (diffHrs < 24) return `${diffHrs}h ago`;
    return d.toLocaleDateString('en-US', { month: 'short', day: 'numeric' });
  };

  // Printer selection
  const savePrinter = (name) => {
    localStorage.setItem('pickTicketPrinter', name);
    setSelectedPrinter(name);
    setShowPrinterModal(false);
    showToast(`Printer set to ${name}`);
  };

  const openPrinterModal = async () => {
    if (!qzConnected) {
      try {
        await connect();
        setQzConnected(true);
        const list = await getPrinters();
        setPrinters(list);
      } catch (err) {
        showToast('QZ Tray not running. Install and start QZ Tray.', 'error');
        return;
      }
    }
    setShowPrinterModal(true);
  };

  // Toggle expanded row to show line items
  const toggleExpand = async (order) => {
    if (expandedOrderId === order.sales_order_id) {
      setExpandedOrderId(null);
      setExpandedItems([]);
      return;
    }
    setExpandedOrderId(order.sales_order_id);
    setExpandedItems([]);
    setExpandLoading(true);
    try {
      // FBA inbound orders have items inline — no NetSuite lookup needed
      if (order.order_type === 'fba_inbound' && order.items) {
        setExpandedItems(order.items.map(i => ({
          sku: i.seller_sku,
          description: i.product_name || i.seller_sku,
          quantity: i.quantity,
          bin: '',
          fnsku: i.fnsku,
        })));
      } else {
        const res = await axios.post('/api/picking/print-preview', {
          salesOrderId: order.sales_order_id
        });
        setExpandedItems(res.data.lineItems || []);
      }
    } catch (err) {
      console.error('Failed to fetch line items:', err);
      setExpandedItems([]);
    } finally {
      setExpandLoading(false);
    }
  };

  // Toggle high-priority flag (optimistic update, revert on failure). FBA
  // inbound rows aren't backed by a cache row, so they can't be flagged.
  const togglePriority = async (order, e) => {
    e.stopPropagation();
    if (order.order_type === 'fba_inbound') return;
    const next = !order.priority;
    setQueue(prev => prev.map(o => o.sales_order_id === order.sales_order_id ? { ...o, priority: next } : o));
    try {
      await axios.post('/api/picking/priority', { salesOrderId: order.sales_order_id, priority: next });
      showToast(next ? `${order.tran_id} marked priority` : `${order.tran_id} priority removed`);
    } catch (err) {
      setQueue(prev => prev.map(o => o.sales_order_id === order.sales_order_id ? { ...o, priority: !next } : o));
      showToast('Failed to update priority', 'error');
    }
  };

  // Dismiss a zombie order (already shipped in reality, never fulfilled in
  // NetSuite, so its status keeps re-queueing it). Dismissals are logged
  // server-side as the close-in-NetSuite worklist.
  const dismissOrder = async (order, e) => {
    e.stopPropagation();
    if (!window.confirm(`Remove ${order.tran_id} (${order.customer_name || ''}) from the pick queue?\n\nUse this for orders that already shipped or should not be picked. It will be logged for closing in NetSuite later.`)) return;
    const prev = queue;
    setQueue(q => q.filter(o => o.sales_order_id !== order.sales_order_id));
    try {
      await axios.post('/api/picking/dismiss', {
        salesOrderId: order.sales_order_id,
        tranId: order.tran_id,
        customerName: order.customer_name,
      });
      showToast(`${order.tran_id} dismissed from queue`);
    } catch (err) {
      setQueue(prev);
      showToast('Failed to dismiss order', 'error');
    }
  };

  // Break cases into singles for a short line, straight from the pick ticket.
  // Reuses /api/ops/convert-case — the same NetSuite inventory adjustment the
  // Case Conversion tool posts — then reloads the line items so the picker sees
  // the new bin split instead of a stale shortfall row.
  const convertCaseToSingles = async (item, order, e) => {
    if (e) e.stopPropagation();
    const alt = item.case_alternative;
    if (!alt || !alt.can_convert || convertingSku) return;

    setConvertingSku(item.sku);
    try {
      const res = await axios.post('/api/ops/convert-case', {
        case_sku: alt.case_sku,
        bin_id: alt.source_bin_id,
        destination_bin_id: alt.source_bin_id,
        quantity: alt.cases_to_break,
        user: localStorage.getItem('userName') || 'Pick Station'
      });
      if (!res.data?.success) throw new Error(res.data?.error || 'Conversion failed');

      showToast(`Broke ${alt.cases_to_break} ${alt.case_sku} into ${alt.cases_to_break * alt.units_per_case} ${item.sku}`);

      // Re-fetch this order's lines so bins/OH reflect the adjustment
      const refreshed = await axios.post('/api/picking/print-preview', {
        salesOrderId: order.sales_order_id
      });
      setExpandedItems(refreshed.data.lineItems || []);
    } catch (err) {
      const detail = err.response?.data?.detail || err.response?.data?.error || err.message;
      showToast(`Case conversion failed: ${detail}`, 'error');
    } finally {
      setConvertingSku(null);
    }
  };

  // Open the on-hand adjust modal for a line item (stop the click from
  // bubbling to any row toggle behind it)
  const openAdjustModal = (item, e) => {
    if (e) e.stopPropagation();
    if (!item.sku) return;
    setAdjustItem(item);
    setAdjustQty(item.bin_on_hand != null ? String(Math.round(item.bin_on_hand)) : '');
    setAdjustError('');
  };

  // Post the on-hand correction straight to NetSuite (same endpoint the pack
  // station's "insufficient inventory" flow uses) and reflect it in the
  // already-open line-item preview so the picker sees it update in place.
  const handleAdjustSubmit = async () => {
    const qty = parseFloat(adjustQty);
    if (!Number.isFinite(qty) || qty < 0) {
      setAdjustError('Enter a valid non-negative quantity');
      return;
    }
    setAdjusting(true);
    setAdjustError('');
    try {
      await axios.post('/api/packing/quick-adjust-inventory', {
        sku: adjustItem.sku,
        newQuantityOnHand: qty,
      });
      setExpandedItems(prev => prev.map(it => it.sku === adjustItem.sku ? { ...it, bin_on_hand: qty } : it));
      showToast(`${adjustItem.sku} on-hand set to ${qty}`);
      setAdjustItem(null);
    } catch (err) {
      setAdjustError(err.response?.data?.error || err.message || 'Inventory adjustment failed');
    } finally {
      setAdjusting(false);
    }
  };

  // Generate SVG barcode string for a value
  const generateBarcodeSVG = (value) => {
    try {
      const svgNode = document.createElementNS('http://www.w3.org/2000/svg', 'svg');
      JsBarcode(svgNode, value, {
        format: 'CODE128',
        width: 2,
        height: 50,
        displayValue: true,
        fontSize: 12,
        margin: 5,
      });
      return new XMLSerializer().serializeToString(svgNode);
    } catch (e) {
      return `<span style="font-size:14px;font-weight:bold">${value}</span>`;
    }
  };

  // Generate pick ticket HTML for thermal printer
  const generatePickTicketHTML = (lineItems, order) => {
    const now = new Date();
    const dateStr = now.toLocaleDateString('en-US');
    const timeStr = now.toLocaleTimeString('en-US', { hour: '2-digit', minute: '2-digit' });
    const category = getCategoryLabel(order.customer_category) + (order.is_local_delivery ? ' · Local Delivery' : '');
    const shipDate = order.ship_date ? new Date(order.ship_date).toLocaleDateString('en-US') : '';
    const totalUnits = lineItems.reduce((sum, item) => sum + Math.round(parseFloat(item.quantity || item.quantityremaining || item.qty || 0)), 0);

    // Sort items by bin for efficient warehouse walking
    const sortedItems = [...lineItems].sort((a, b) => {
      const binA = (a.bin || a.bin_number || 'ZZZ').toUpperCase();
      const binB = (b.bin || b.bin_number || 'ZZZ').toUpperCase();
      return binA.localeCompare(binB);
    });

    const rows = sortedItems.map((item) => {
      const sku = item.sku || item.item_sku || item.itemid || '';
      const desc = item.description || item.item_description || item.item_display_name || '';
      const rawQty = item.quantity || item.quantityremaining || item.quantityoriginal || item.qty || 0;
      const qty = Math.round(parseFloat(rawQty)) || 0;
      const bin = item.bin || item.bin_number || '';
      const onHand = item.bin_on_hand != null ? Math.round(parseFloat(item.bin_on_hand)) : '';
      const alt = item.case_alternative;
      // Shortfall rows get a build-from-cases note so the floor doesn't have to
      // go look up whether cases of the same candle are sitting in a bin.
      const caseNote = alt
        ? `<tr>
        <td colspan="2" style="padding:1px 0 3px 0;font-size:9px;font-weight:700;border-bottom:1px dotted #ccc">
          SHORT ${alt.units_short} - BUILD FROM CASES: break ${alt.cases_to_break} of ${alt.cases_on_hand} ${alt.case_sku}${alt.bins.length ? ` [${alt.bins.join(', ')}]` : ''} (${alt.units_per_case}/case)
        </td>
      </tr>`
        : '';
      return `<tr>
        <td style="padding:3px 0 0 0;font-size:11px;font-weight:700">${sku}</td>
        <td style="padding:3px 0 0 0;text-align:right;font-weight:800;font-size:14px;white-space:nowrap">x${qty}</td>
      </tr>
      <tr>
        <td colspan="2" style="padding:0 0 3px 0;font-size:9px;color:#333;${caseNote ? '' : 'border-bottom:1px dotted #ccc'}">${desc}${bin ? ` <strong>[${bin}${onHand !== '' ? `: ${onHand} OH` : ''}]</strong>` : ''}</td>
      </tr>${caseNote}`;
    }).join('');

    const barcodeSvg = generateBarcodeSVG(order.tran_id || 'UNKNOWN');

    return `<!DOCTYPE html>
<html>
<head>
<style>
  @page { margin: 0; padding: 0; }
  * { margin: 0; padding: 0; box-sizing: border-box; }
  body { font-family: Arial, sans-serif; width: 100%; margin: 0; padding: 1mm 3mm 3mm 3mm; color: #000; }
  table { width: 100%; border-collapse: collapse; }
</style>
</head>
<body>
  ${order.priority ? `<div style="background:#000;color:#fff;text-align:center;font-size:16px;font-weight:900;letter-spacing:3px;padding:5px 0;margin-bottom:4px">&#9873; PRIORITY &#9873;</div>` : ''}
  <div style="text-align:center;margin-bottom:4px">
    ${barcodeSvg}
  </div>

  <div style="border-top:2px solid #000;border-bottom:1px solid #000;padding:4px 0;margin-bottom:6px">
    <div style="font-size:14px;font-weight:800">${(order.customer_name || '').substring(0, 32)}</div>
    <div style="font-size:10px;color:#333;margin-top:2px">
      ${category ? `<strong>${category}</strong>` : ''}
      ${shipDate ? ` Ship: ${shipDate}` : ''}
       Age: ${order.age_days || 0}d
    </div>
    ${order.faire_cancel_on ? `<div style="font-size:11px;font-weight:800;margin-top:2px">FAIRE CANCELS ${formatCancelDate(order.faire_cancel_on)}${order.faire_cancel_risk === 'not_accepted' ? ' - NOT ACCEPTED' : ''}</div>` : ''}
  </div>

  <table>
    <thead>
      <tr style="border-bottom:2px solid #000">
        <th style="text-align:left;font-size:9px;font-weight:800;padding:2px 0;text-transform:uppercase">Item</th>
        <th style="text-align:right;font-size:9px;font-weight:800;padding:2px 0;text-transform:uppercase">Qty</th>
      </tr>
    </thead>
    <tbody>
      ${rows}
    </tbody>
  </table>

  <div style="border-top:2px solid #000;padding-top:4px;margin-top:6px">
    <div style="font-size:12px;font-weight:700">${lineItems.length} lines - ${totalUnits} units</div>
    <div style="font-size:9px;color:#666;margin-top:2px">${dateStr} ${timeStr}</div>
  </div>
</body>
</html>`;
  };

  // Print pick ticket via QZ Tray
  const handlePrint = async (order) => {
    if (printingOrderId) return;

    if (!qzConnected) {
      try {
        await connect();
        setQzConnected(true);
        const list = await getPrinters();
        setPrinters(list);
        if (!selectedPrinter && list.length > 0) {
          setSelectedPrinter(list[0]);
          localStorage.setItem('pickTicketPrinter', list[0]);
        }
      } catch (err) {
        showToast('QZ Tray not running. Start QZ Tray and click Allow when prompted.', 'error');
        return;
      }
    }

    if (!selectedPrinter) {
      openPrinterModal();
      return;
    }

    setPrintingOrderId(order.sales_order_id);
    try {
      // FBA inbound orders have items inline — use them directly
      let lineItems;
      if (order.order_type === 'fba_inbound' && order.items) {
        lineItems = order.items.map(i => ({
          sku: i.seller_sku,
          description: i.product_name || i.seller_sku,
          quantity: i.quantity,
          bin: '',
          fnsku: i.fnsku,
        }));
      } else if (expandedOrderId === order.sales_order_id && expandedItems.length > 0) {
        // Row is already expanded — print exactly what's on screen instead of
        // re-querying NetSuite a second time. Bin assignment is live/on-hand
        // based, so a second query taken seconds later can return a different
        // bin split even though nobody touched the order.
        lineItems = expandedItems;
        await axios.post('/api/picking/print', { salesOrderId: order.sales_order_id, lineItems });
      } else {
        const res = await axios.post('/api/picking/print', {
          salesOrderId: order.sales_order_id
        });
        lineItems = res.data.lineItems || [];
      }

      const html = generatePickTicketHTML(lineItems, order);
      await printHTML(html, selectedPrinter);

      showToast(`Printed ${order.tran_id}`);
      await loadAll(false);
    } catch (err) {
      console.error('Failed to print pick ticket:', err);
      showToast(err.response?.data?.error || err.message || 'Failed to print', 'error');
    } finally {
      setPrintingOrderId(null);
    }
  };

  // Loading state
  if (loading && queue.length === 0) {
    return <div className="pick-ticket-loading">Loading pick ticket queue...</div>;
  }

  return (
    <div className="pick-ticket-screen">
      {/* Info Blocks */}
      <div className="pick-ticket-info-blocks">
        <div className="pick-ticket-info-block">
          <div className="pick-ticket-info-value">{sortedQueue.length}</div>
          <div className="pick-ticket-info-label">Total Orders</div>
        </div>
        <div className="pick-ticket-info-block block-fully">
          <div className="pick-ticket-info-value">{sortedQueue.filter(o => getPickabilityTier(o) === 0).length}</div>
          <div className="pick-ticket-info-label">Fully Pickable</div>
        </div>
        <div className="pick-ticket-info-block block-partial">
          <div className="pick-ticket-info-value">{sortedQueue.filter(o => getPickabilityTier(o) === 1).length}</div>
          <div className="pick-ticket-info-label">Partial</div>
        </div>
        <div className="pick-ticket-info-block">
          <div className="pick-ticket-info-value">{stats.printed_today || 0}</div>
          <div className="pick-ticket-info-label">Printed Today</div>
        </div>
        <div
          className="pick-ticket-info-block pick-ticket-refresh-block"
          onClick={handleRefresh}
        >
          <div className={`pick-ticket-info-value pick-ticket-refresh-icon${refreshing ? ' spinning' : ''}`}>&#8635;</div>
          <div className="pick-ticket-info-label">
            {lastUpdate ? lastUpdate.toLocaleTimeString([], { hour: '2-digit', minute: '2-digit' }) : 'Refresh'}
          </div>
        </div>
      </div>

      {/* Printer status */}
      <div className={`pick-ticket-printer-banner ${qzConnected && selectedPrinter ? 'connected' : ''}`}>
        <span>
          {!qzConnected
            ? 'QZ Tray: click Allow when prompted'
            : selectedPrinter
              ? `Printer: ${selectedPrinter}`
              : 'No printer selected'}
        </span>
        <button className="pick-ticket-printer-btn" onClick={openPrinterModal}>
          {selectedPrinter ? 'Change' : 'Select Printer'}
        </button>
      </div>

      {error && <div className="pick-ticket-error">{error}</div>}

      {/* Search */}
      <div className="pick-ticket-search">
        <input
          type="text"
          className="pick-ticket-search-input"
          placeholder="Search order #, customer, or item (SKU or name)…"
          value={search}
          inputMode={showKeyboard ? 'none' : undefined}
          onChange={(e) => setSearch(e.target.value)}
          // Some station touchscreens report taps as mouse clicks, so also open on any
          // touch-capable device.
          onPointerDown={(e) => {
            if (e.pointerType === 'touch' || e.pointerType === 'pen' || navigator.maxTouchPoints > 0) setShowKeyboard(true);
          }}
        />
        <button
          className={`pick-ticket-search-clear pick-ticket-kb-toggle ${showKeyboard ? 'active' : ''}`}
          onClick={() => setShowKeyboard(v => !v)}
          title="On-screen keyboard"
          aria-label="On-screen keyboard"
        >
          Keyboard
        </button>
        {search && (
          <button
            className="pick-ticket-search-clear"
            onClick={() => setSearch('')}
            title="Clear search"
            aria-label="Clear search"
          >
            &#10005;
          </button>
        )}
        {searchTerms.length > 0 && (
          <span className="pick-ticket-search-count">
            {sortedQueue.length} match{sortedQueue.length === 1 ? '' : 'es'}
          </span>
        )}
      </div>

      {/* Filters */}
      <div className="pick-ticket-filters">
        {['all', 'dtc', 'wholesale', 'amazon', 'fba', 'privatelabel', 'localdelivery', 'mysterybox'].map(f => (

          <button
            key={f}
            className={`pick-ticket-filter-btn ${filterType === f ? 'active' : ''}`}
            onClick={() => setFilterType(f)}
          >
            {f === 'all' ? 'All' : f === 'dtc' ? 'DTC' : f === 'fba' ? 'FBA Inbound' : f === 'privatelabel' ? 'Private Label' : f === 'localdelivery' ? 'Local Delivery' : f === 'mysterybox' ? 'Mystery Box' : f.charAt(0).toUpperCase() + f.slice(1)}
          </button>
        ))}
      </div>

      {/* Table */}
      <div className="pick-ticket-table-container">
        {sortedQueue.length === 0 ? (
          <div className="pick-ticket-empty">
            {searchTerms.length > 0 || filterType !== 'all' ? (
              <>
                <p className="pick-ticket-empty-title">No orders match</p>
                <p className="pick-ticket-empty-hint">
                  {queue.length} order{queue.length === 1 ? '' : 's'} in the queue &mdash; none match the current
                  {searchTerms.length > 0 ? ' search' : ''}{searchTerms.length > 0 && filterType !== 'all' ? ' and' : ''}{filterType !== 'all' ? ' filter' : ''}.
                </p>
              </>
            ) : (
              <>
                <p className="pick-ticket-empty-title">No orders in queue</p>
                <p className="pick-ticket-empty-hint">All orders have been dispatched or the cache is empty</p>
              </>
            )}
          </div>
        ) : (
          <table className="pick-ticket-table">
            <thead>
              <tr>
                <th style={{ width: 36 }}></th>
                <th style={{ width: 90 }}>Order</th>
                <th>Customer</th>
                <th>Memo</th>
                <th style={{ width: 80 }}>Type</th>
                <th style={{ width: 55, textAlign: 'center' }}>Items</th>
                <th style={{ width: 100, textAlign: 'center' }}>Shippable</th>
                <th style={{ width: 70, textAlign: 'center' }}>Cases</th>
                <th style={{ width: 75 }}>Ship</th>
                <th style={{ width: 50, textAlign: 'center' }}>Age</th>
                <th style={{ width: 80, textAlign: 'center' }}>Printed</th>
                <th style={{ width: 80, textAlign: 'center' }}>Print</th>
              </tr>
            </thead>
            <tbody>
              {sortedQueue.map(order => {
                const shippable = getShippableInfo(order);
                const tier = getPickabilityTier(order);
                const rowClass = order.priority ? 'row-priority' : tier === 0 ? 'row-fully' : tier === 1 ? 'row-partial' : 'row-none';
                const ageDays = order.age_days || 0;
                const ageClass = ageDays >= 7 ? 'urgent' : ageDays >= 3 ? 'warning' : '';
                const isExpanded = expandedOrderId === order.sales_order_id;
                return (
                  <React.Fragment key={order.sales_order_id}>
                    <tr className={`${rowClass}${isExpanded ? ' expanded' : ''}`} onClick={() => toggleExpand(order)} style={{ cursor: 'pointer' }}>
                      <td className="pick-ticket-col-priority">
                        {order.order_type !== 'fba_inbound' && (
                          <>
                            <button
                              className={`pick-ticket-priority-btn${order.priority ? ' active' : ''}`}
                              title={order.priority ? 'Remove priority' : 'Mark as priority'}
                              onClick={(e) => togglePriority(order, e)}
                            >
                              &#9873;
                            </button>
                            <button
                              className="pick-ticket-dismiss-btn"
                              title="Dismiss from queue (already shipped / should not be picked)"
                              onClick={(e) => dismissOrder(order, e)}
                            >
                              &#10005;
                            </button>
                          </>
                        )}
                      </td>
                      <td className="pick-ticket-col-order">
                        {order.tran_id}
                        {order.verification && order.verification.verdict === 'verified' && (
                          <span className="pick-verify-badge verified" title={`Lines verified against ${order.verification.source}`}>&#10003;</span>
                        )}
                        {order.verification && order.verification.verdict !== 'verified' && (
                          <span
                            className={`pick-verify-badge ${order.verification.verdict === 'zombie' ? 'zombie' : 'review'}`}
                            title={
                              order.verification.verdict === 'zombie'
                                ? `${order.verification.detail || 'Channel says already fulfilled'} — do not ship; dismiss with ✕`
                                : `No independent record to verify against — check before shipping.${order.verification.detail ? ` ${order.verification.detail}` : ''}`
                            }
                          >
                            {order.verification.verdict === 'zombie' ? '!' : '?'}
                          </span>
                        )}
                        {order.faire_cancel_on && (
                          <div
                            className={`pick-faire-cancel ${order.faire_cancel_days <= 7 ? 'urgent' : order.faire_cancel_days <= 14 ? 'warning' : ''}`}
                            title={order.faire_cancel_risk === 'not_accepted'
                              ? `Faire ${order.faire_order_number} is not accepted. Faire cancels it on this date and pays nothing, even if it shipped. Accept it in the Faire portal once it can ship.`
                              : `Faire ${order.faire_order_number} is accepted but has no tracking. Faire cancels it on this date and pays nothing.`}
                          >
                            Faire cancels {formatCancelDate(order.faire_cancel_on)}{order.faire_cancel_days >= 0 ? ` (${order.faire_cancel_days}d)` : ' (past)'}
                          </div>
                        )}
                      </td>
                      <td className="pick-ticket-col-customer" title={order.customer_name || ''}>{order.customer_name || '-'}</td>
                      <td className="pick-ticket-col-memo" title={order.memo || ''}>{order.memo || ''}</td>
                      <td className="pick-ticket-col-type">
                        {(order.customer_category || order.order_type === 'fba_inbound') ? (
                          <span className={`pick-ticket-category ${getCategoryClass(order.customer_category, order.order_type)}`}>
                            {getCategoryLabel(order.customer_category, order.order_type)}
                          </span>
                        ) : '-'}
                        {order.is_local_delivery && (
                          <span className="pick-ticket-category localdelivery" title="Ships via in-house local delivery">
                            Local
                          </span>
                        )}
                        {order.case_buildable_lines > 0 && (
                          <span
                            className="pick-ticket-category caseconvert"
                            title={`${order.case_buildable_lines} short line${order.case_buildable_lines === 1 ? '' : 's'} can be covered by breaking down cases — expand to convert`}
                          >
                            Case Convert
                          </span>
                        )}
                      </td>
                      <td className="pick-ticket-col-items">{order.total_line_items || 0}</td>
                      <td className="pick-ticket-col-shippable">
                        <span className={`pick-ticket-shippable ${shippable.className}`}>
                          {shippable.label}
                        </span>
                      </td>
                      <td className="pick-ticket-col-cases">
                        {order.case_buildable_lines > 0 ? (
                          <span
                            className="pick-ticket-case-badge"
                            title={`${order.case_buildable_lines} short line${order.case_buildable_lines === 1 ? '' : 's'} can be covered by breaking down cases — expand to convert`}
                          >
                            {order.case_buildable_lines}
                          </span>
                        ) : ''}
                      </td>
                      <td className="pick-ticket-col-date">{formatDate(order.ship_date)}</td>
                      <td className="pick-ticket-col-age">
                        <span className={`pick-ticket-age ${ageClass}`} title={ageTooltip(order)}>{ageDays}d</span>
                      </td>
                      <td className="pick-ticket-col-printed">
                        <span className={`pick-ticket-printed-time ${order.last_printed_at ? 'has-print' : ''}`}>
                          {formatPrintedTime(order.last_printed_at)}
                        </span>
                      </td>
                      <td className="pick-ticket-col-action">
                        <button
                          className="pick-ticket-print-btn"
                          onClick={(e) => { e.stopPropagation(); handlePrint(order); }}
                          disabled={printingOrderId === order.sales_order_id}
                        >
                          {printingOrderId === order.sales_order_id ? '...' : order.last_printed_at ? 'Reprint' : 'Print'}
                        </button>
                      </td>
                    </tr>
                    {isExpanded && (
                      <tr className="pick-ticket-expand-row">
                        <td colSpan={12} style={{ padding: 0 }}>
                          <div className="pick-ticket-expand-panel">
                            {/* The memo column in the row is capped at 220px and ellipsised,
                                so anything past a few words is unreadable there. Expanding is
                                where you actually read it - e.g. "SHIP AS READY: {item} ({date})",
                                which tells the packer to send what's on the shelf and leave the
                                dated line open. */}
                            {order.memo && (
                              <div className="pick-ticket-expand-memo">
                                <span className="pick-ticket-expand-memo-label">Memo</span>
                                <span className="pick-ticket-expand-memo-text">{order.memo}</span>
                              </div>
                            )}
                            {expandLoading ? (
                              <div className="pick-ticket-expand-loading">Loading items...</div>
                            ) : expandedItems.length === 0 ? (
                              <div className="pick-ticket-expand-loading">No line items found</div>
                            ) : (
                              <>
                                <table className="pick-ticket-expand-table">
                                  <thead>
                                    <tr>
                                      <th>SKU</th>
                                      {order.order_type === 'fba_inbound' && <th>FNSKU</th>}
                                      <th>Description</th>
                                      <th>Bin</th>
                                      {order.order_type !== 'fba_inbound' && <th style={{ textAlign: 'right' }}>OH</th>}
                                      <th style={{ textAlign: 'right' }}>Qty</th>
                                    </tr>
                                  </thead>
                                  <tbody>
                                    {expandedItems.map((item, idx) => (
                                      <React.Fragment key={idx}>
                                        <tr>
                                          <td style={{ fontWeight: 700 }}>{item.sku || item.itemid || ''}</td>
                                          {order.order_type === 'fba_inbound' && <td style={{ fontFamily: 'monospace', color: '#283593' }}>{item.fnsku || '-'}</td>}
                                          <td>{item.description || ''}</td>
                                          <td style={{ fontWeight: 700 }}>{item.bin || '-'}</td>
                                          {order.order_type !== 'fba_inbound' && (
                                            <td style={{ textAlign: 'right' }}>
                                              <button
                                                type="button"
                                                className="pick-ticket-oh-btn"
                                                title="Click to adjust this item's on-hand inventory"
                                                onClick={(e) => openAdjustModal(item, e)}
                                              >
                                                {item.bin_on_hand != null ? Math.round(item.bin_on_hand) : '-'}
                                              </button>
                                            </td>
                                          )}
                                          <td style={{ textAlign: 'right', fontWeight: 800, fontSize: 14 }}>x{Math.round(parseFloat(item.quantity || item.quantityremaining || 0))}</td>
                                        </tr>
                                        {item.case_alternative && (
                                          <tr className="pick-ticket-case-alt-row">
                                            <td colSpan={order.order_type === 'fba_inbound' ? 6 : 5}>
                                              <span className="pick-ticket-case-alt">
                                                Short {item.case_alternative.units_short} — build from cases:
                                                break {item.case_alternative.cases_to_break}
                                                {' '}of {item.case_alternative.cases_on_hand} {item.case_alternative.case_sku}
                                                {item.case_alternative.bins.length > 0 && ` in ${item.case_alternative.bins.join(', ')}`}
                                                {' '}({item.case_alternative.units_per_case}/case, {item.case_alternative.units_available} units on hand)
                                              </span>
                                              {item.case_alternative.can_convert ? (
                                                <button
                                                  type="button"
                                                  className="pick-ticket-case-convert-btn"
                                                  title={`Post a NetSuite inventory adjustment: -${item.case_alternative.cases_to_break} ${item.case_alternative.case_sku}, +${item.case_alternative.cases_to_break * item.case_alternative.units_per_case} ${item.sku} in ${item.case_alternative.source_bin}`}
                                                  disabled={convertingSku === item.sku}
                                                  onClick={(e) => convertCaseToSingles(item, order, e)}
                                                >
                                                  {convertingSku === item.sku
                                                    ? 'Converting...'
                                                    : `Break ${item.case_alternative.cases_to_break} case${item.case_alternative.cases_to_break === 1 ? '' : 's'} from ${item.case_alternative.source_bin}`}
                                                </button>
                                              ) : (
                                                <span className="pick-ticket-case-convert-na">
                                                  Convert by hand — no single bin holds {item.case_alternative.cases_to_break}
                                                </span>
                                              )}
                                            </td>
                                          </tr>
                                        )}
                                      </React.Fragment>
                                    ))}
                                  </tbody>
                                </table>
                                {order.order_type === 'fba_inbound' && (
                                  <div style={{ padding: '12px 0 4px', display: 'flex', gap: 8 }}>
                                    <button
                                      className="pick-ticket-print-btn"
                                      style={{ background: '#e8eaf6', color: '#283593', border: 'none', padding: '8px 16px', borderRadius: 6, fontWeight: 600, cursor: 'pointer' }}
                                      onClick={async (e) => {
                                        e.stopPropagation();
                                        try {
                                          const skus = (order.items || []).map(i => i.seller_sku).join(',');
                                          const qty = (order.items || [])[0]?.quantity || 20;
                                          const url = `/api/amazon/fnsku-labels?skus=${skus}&qty=${qty}`;
                                          window.open(url, '_blank');
                                        } catch (err) {
                                          console.error('FNSKU label error:', err);
                                        }
                                      }}
                                    >
                                      Print FNSKU Labels
                                    </button>
                                  </div>
                                )}
                              </>
                            )}
                          </div>
                        </td>
                      </tr>
                    )}
                  </React.Fragment>
                );
              })}
            </tbody>
          </table>
        )}
      </div>

      {/* Printer Selection Modal */}
      {showPrinterModal && (
        <div className="pick-ticket-modal-overlay" onClick={() => setShowPrinterModal(false)}>
          <div className="pick-ticket-modal" onClick={e => e.stopPropagation()}>
            <h2>Select Printer</h2>
            {printers.length === 0 ? (
              <p style={{ color: '#718096', fontSize: 14 }}>No printers found. Make sure QZ Tray is running.</p>
            ) : (
              <div className="pick-ticket-printer-list">
                {printers.map(p => (
                  <button
                    key={p}
                    className={`pick-ticket-printer-option ${selectedPrinter === p ? 'active' : ''}`}
                    onClick={() => savePrinter(p)}
                  >
                    {p}
                  </button>
                ))}
              </div>
            )}
            <div className="pick-ticket-modal-actions">
              <button className="pick-ticket-modal-cancel" onClick={() => setShowPrinterModal(false)}>
                Cancel
              </button>
            </div>
          </div>
        </div>
      )}

      {/* Adjust On-Hand Modal */}
      {adjustItem && (
        <div className="pick-ticket-modal-overlay" onClick={() => !adjusting && setAdjustItem(null)}>
          <div className="pick-ticket-modal" onClick={e => e.stopPropagation()}>
            <h2>Adjust On-Hand</h2>
            <div className="pick-ticket-modal-row">
              <span className="pick-ticket-modal-label">SKU</span>
              <span className="pick-ticket-modal-value">{adjustItem.sku}</span>
            </div>
            {adjustItem.bin && (
              <div className="pick-ticket-modal-row">
                <span className="pick-ticket-modal-label">Bin</span>
                <span className="pick-ticket-modal-value">{adjustItem.bin}</span>
              </div>
            )}
            <div className="pick-ticket-modal-row" style={{ border: 'none', alignItems: 'center' }}>
              <span className="pick-ticket-modal-label">Set on-hand to</span>
              <input
                type="number"
                min="0"
                autoFocus
                value={adjustQty}
                onChange={e => setAdjustQty(e.target.value)}
                onKeyDown={e => e.key === 'Enter' && !adjusting && handleAdjustSubmit()}
                className="pick-ticket-oh-input"
              />
            </div>
            {adjustError && (
              <div className="pick-ticket-oh-error">{adjustError}</div>
            )}
            <div className="pick-ticket-modal-actions">
              <button className="pick-ticket-modal-cancel" onClick={() => setAdjustItem(null)} disabled={adjusting}>
                Cancel
              </button>
              <button className="pick-ticket-modal-confirm" onClick={handleAdjustSubmit} disabled={adjusting}>
                {adjusting ? 'Posting...' : 'Post Adjustment'}
              </button>
            </div>
          </div>
        </div>
      )}

      {/* Toast */}
      {toast && (
        <div className={`pick-ticket-toast ${toast.type}`}>
          {toast.message}
        </div>
      )}

      {/* On-screen keyboard: types straight into the search so the list filters live */}
      {showKeyboard && (
        <>
          <div className="pick-ticket-kb-spacer" />
          <div className="pick-ticket-kb">
            <div className="pick-ticket-kb-display">
              {search || <span className="pick-ticket-kb-placeholder">Type an order #, customer, or SKU</span>}
              {searchTerms.length > 0 && (
                <span className="pick-ticket-kb-count">
                  {sortedQueue.length} match{sortedQueue.length === 1 ? '' : 'es'}
                </span>
              )}
            </div>
            {KEYBOARD_ROWS.map((row, i) => (
              <div key={i} className="pick-ticket-kb-row">
                {row.map(key => (
                  <button key={key} className="pick-ticket-kb-key" onClick={() => setSearch(s => s + key)}>
                    {key}
                  </button>
                ))}
              </div>
            ))}
            <div className="pick-ticket-kb-row">
              <button className="pick-ticket-kb-key wide" onClick={() => setSearch('')}>Clear</button>
              <button className="pick-ticket-kb-key space" onClick={() => setSearch(s => s + ' ')}>Space</button>
              <button className="pick-ticket-kb-key wide" onClick={() => setSearch(s => s.slice(0, -1))}>Delete</button>
              <button className="pick-ticket-kb-key wide done" onClick={() => setShowKeyboard(false)}>Done</button>
            </div>
          </div>
        </>
      )}
    </div>
  );
}

const KEYBOARD_ROWS = [
  ['1', '2', '3', '4', '5', '6', '7', '8', '9', '0'],
  ['Q', 'W', 'E', 'R', 'T', 'Y', 'U', 'I', 'O', 'P'],
  ['A', 'S', 'D', 'F', 'G', 'H', 'J', 'K', 'L', '-'],
  ['Z', 'X', 'C', 'V', 'B', 'N', 'M', '#', '.'],
];

export default PickTicketScreen;
